import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import {
    PriceListCollection,
    PriceListEntry,
    MasterCatalogCollection,
    Order,
    OrderCollection,
    OrderEntry,
    OrderService,
    QuickbooksUpdateService,
    OrderStatusEnum,
    BarcodeMapper, OrderEntryStatusEnum
} from '@/firestore'
import LabeledValue from '@/components/LabeledValue.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import OrderListItem from '@/components/Order/OrderListItem.vue'
import axios from 'axios'
import QuantityTypeSelector from '@/components/Order/QuantityTypeSelector.vue'
import store, { Actions } from '../../../store'

@Component({
    components: {
        OrderListItem,
        LoadingPage,
        LabeledValue,
        QuantityTypeSelector
    }
})
export default class LoadingDockList extends Mixins(IconsMixin, AuthMixin) {
    public wOrder: { data: Order | null } = {data: null}
    public wRestaurantOrder: { data: Order | null } = {data: null}

    public orderService: OrderService | null = null

    public backorder: Order | null = null
    public checkedIn = OrderStatusEnum.CHECKED_IN

    // button loading actions
    public isReceiving = false
    public isCheckingIn = false
    public isAccepting = false

    // modals
    public showBackorderCreationModal = false
    public backorderCreationConfirmation = (confirm: boolean) => {
    }

    // alert snackbar
    showAlertSnackbar = false
    alertSnackbarMessage = ''

    public get selectedIndex() {
        return this.order
            ? this.order.orderEntries.findIndex((e) => e === this.selected)
            : -1
    }

    public set selectedIndex(index: number) {
        if (this.order && index < this.order.orderEntries.length) {
            this.select(this.order.orderEntries[index])
        } else {
            this.selected = null
        }
    }

    public selected: OrderEntry | null = null
    public formValid: boolean = false

    public get order(): Order | null {
        if (!this.wOrder.data) {
            return null
        }

        if (this.selected) {
            const index = this.wOrder.data.orderEntries
                .findIndex((entry) => this.selected && entry.id === this.selected.id)
            this.wOrder.data.orderEntries.splice(index, 1, this.selected)
        }

        return this.wOrder.data
    }

    public get restaurantOrder(): Order | null {
        if (!this.wRestaurantOrder.data) {
            return null
        }

        return this.wRestaurantOrder.data
    }

    public get canReceive(): boolean {
        return this.order ? this.order.canReceive : false
    }

    public get canCheckIn(): boolean {
        if (!this.order) {
            return false
        }

        return this.order.orderEntries.reduce((valid: boolean, entry: OrderEntry) => valid && !entry.isPending, true)
    }

    public async mounted() {
        const collection = new OrderCollection((await this.$firebase).firestore())
        this.orderService = new OrderService((await this.$firebase).firestore())

        try {
            const vendorOrder = await collection.get(this.$route.params.id)
            if (!vendorOrder || !vendorOrder.restaurantOrderId) {
                const err = new Error('You do not have permissions to view that page')
                return store.dispatch(Actions.SET_ERROR, [err, this.$router])
            }

            this.wOrder = collection.watch(this.$route.params.id)
            this.wRestaurantOrder = collection.watch(vendorOrder.restaurantOrderId)
        } catch (error) {
            const err = new Error('You do not have permissions to view that page')
            return store.dispatch(Actions.SET_ERROR, [err, this.$router])
        }
    }

    public select(entry: OrderEntry) {
        if (entry.receivedQuantity === undefined) {
            entry.receivedQuantity = entry.quantity
            entry.receivedQuantityType = entry.receivedQuantityType ? entry.receivedQuantityType : entry.quantityType

            if (entry.additionalQuantity) {
                entry.receivedQuantity += entry.additionalQuantity
            }
        }

        this.selected = entry
    }

    public async receiveOrder() {
        if (!this.order) {
            return
        }

        this.isReceiving = true
        this.order.receive(this.user.uid)
        await this.saveOrder()
        this.isReceiving = false
    }

    public async checkInOrder() {
        if (!this.order) {
            return
        }

        this.isCheckingIn = true
        this.order.checkIn(this.user.uid)
        await this.saveOrder()
        this.isCheckingIn = false
        if (this.$route.name !== 'receiving-list') {
            await this.$router.push({name: 'receiving-list'})
        }
    }

    public async rejectOrder() {
        if (!this.order) {
            return
        }

        this.isCheckingIn = true
        this.order.reject(this.user.uid)
        await this.saveOrder()
        this.isCheckingIn = false
        if (this.$route.name !== 'receiving-list') {
            await this.$router.push({name: 'receiving-list'})
        }
    }

    public async resetOrderEntry(entry: OrderEntry) {
        if (!this.order || !this.restaurantOrder) {
            return
        }

        entry.resetAcceptance()
        this.selected = null
        await this.saveOrder()
    }

    public async acceptOrderEntry(entry: OrderEntry) {
        if (!this.order || !this.restaurantOrder) {
            return
        }

        this.isAccepting = true
        this.addEntryToPriceList(entry)
        entry.accept()
        await this.saveOrder()

        const quickbooksService = new QuickbooksUpdateService(
            this.order.orderedByCompanyId,
            (await this.$firebase).firestore()
        )
        await quickbooksService.sendInventory(entry)

        if (entry.receivedQuantity !== entry.quantity) {
            await this.addEntryToBackorder(this.order, entry)
        }

        const allEntriesReceived = this.order.orderEntries.every((e) =>
                (e.quantity + (e.additionalQuantity || 0)) === e.receivedQuantity
                && (
                    e.status === OrderEntryStatusEnum.ACCEPTED
                    || e.status === OrderEntryStatusEnum.REJECTED
                )
        )

        if (allEntriesReceived) {
            return this.checkInOrder()
        }

        if (this.selected && entry.id === this.selected.id) {
            this.selectNextEntry()
        }

        this.isAccepting = false
    }

    public async addEntryToPriceList(entry: OrderEntry) {
        const priceListCollection = new PriceListCollection((await this.$firebase).firestore())
        let priceList = await priceListCollection.get(entry.priceListId)
        let entryExists = false

        if (priceList) {
            entryExists = !!priceList.entries.find(priceListEntry => {
                return priceListEntry.upc === entry.upc
                    || priceListEntry.description.toLowerCase() === entry.description.toLowerCase()
            })
        }

        if (!entryExists) {
            const masterCatalogCollection = new MasterCatalogCollection((await this.$firebase).firestore())
            const masterCatalog = await masterCatalogCollection.getByCompanyId(entry.vendorCompanyId)
            if (masterCatalog) {
                entryExists = !!masterCatalog.entries.find(masterCatalogEntry => {
                    return masterCatalogEntry.upc === entry.upc
                        || masterCatalogEntry.description.toLowerCase() === entry.description.toLowerCase()
                })
            }

            if (!priceList) {
                console.error('No price list for vendor/company combo - ' + JSON.stringify(entry))
            } else {
                const priceListEntry = new PriceListEntry(entry.upc, entry.description)

                try {
                    const barcode = (new BarcodeMapper()).map((await axios.get(`/api/barcode/${entry.upc}`)).data)
                    if (barcode) {
                        priceListEntry.barcode = barcode
                    }
                } catch (e) {
                }

                if (entry.category) {
                    priceListEntry.category = entry.category
                }

                if (entry.orderingNotes) {
                    priceListEntry.orderingNotes = entry.orderingNotes
                }

                priceList.entries.push(priceListEntry)
                priceListCollection.save(priceList)
            }
        }
    }

    public async rejectOrderEntry(entry: OrderEntry) {
        if (!this.order || !this.restaurantOrder) {
            return
        }

        entry.reject()
        await this.saveOrder()

        if (this.orderService) {
            await this.addEntryToBackorder(this.order, entry)
        }

        const allEntriesRejected = this.order.orderEntries.every((e) =>
            e.status === OrderEntryStatusEnum.REJECTED
        )

        if (allEntriesRejected) {
            return this.rejectOrder()
        }

        const allEntriesReceived = this.order.orderEntries.every((e) =>
                (e.quantity + (e.additionalQuantity || 0)) === e.receivedQuantity
                && (
                    e.status === OrderEntryStatusEnum.ACCEPTED
                    || e.status === OrderEntryStatusEnum.REJECTED
                )
        )

        if (allEntriesReceived) {
            return this.checkInOrder()
        }

        this.selectNextEntry()
    }

    public async saveOrder() {
        if (!this.order || !this.restaurantOrder) {
            return
        }

        const collection = new OrderCollection((await this.$firebase).firestore())
        await collection.save(this.order)
        await axios.post(`/api/order/${this.order.id}/entries/sync`)
    }

    public selectNextEntry() {
        if (!this.order) {
            return
        }

        this.selectedIndex++
    }

    public updateQuantityTypeSelection(quantityType: any) {
        if (!this.selected) {
            return
        }

        this.selected.receivedQuantityType = quantityType
    }

    public closeBackorderConfirmationModal(confirm: boolean) {
        if (this.backorderCreationConfirmation) {
            this.backorderCreationConfirmation(confirm)
        }

        this.showBackorderCreationModal = false
    }

    public debounce(func: any, timeout = 300) {
        let timer: any
        return (...args: any[]) => {
            clearTimeout(timer)
            timer = setTimeout(() => {
                func(...args)
            }, timeout)
        }
    }

    public processChange = this.debounce(async () => await this.updateInvoiceNumber())

    public async updateInvoiceNumber() {
        if (!this.order) {
            return
        }

        const orderCollection = new OrderCollection((await this.$firebase).firestore())
        await orderCollection.save(this.order)
    }

    private addEntryToBackorder(order: Order, entry: OrderEntry) {
        if (!this.orderService) {
            return
        }

        const confirmation = new Promise<boolean>((res) => {
            this.backorderCreationConfirmation = res
        })

        this.showBackorderCreationModal = true

        return confirmation.then(async (confirm: boolean) => {
            if (confirm && this.orderService) {
                return this.orderService.addEntryToBackorder(order, entry, this.userCompany.id)
            }
            return null
        }).then((backorder) => {
            if (backorder) {
                this.alertSnackbarMessage = this.backorder
                    ? `Item added to ${backorder.number}`
                    : `Backorder ${backorder.number} created`
                this.showAlertSnackbar = true
                this.backorder = backorder
            }
        })
    }
}